/**
 * @module state/uiStore
 * @summary Zustand store mirroring engine state for reactive UI chrome.
 * @description
 *   Holds ONLY UI-reactive slices (tool, zoom %, undo/redo availability, the
 *   selection summary for the Properties panel, status text). The document
 *   (shapes/connections) lives in the engine, never here. `sync*` actions pull
 *   fresh snapshots from the engine; `useEngineSync` wires engine events to them
 *   so only the affected slice re-renders.
 */
import { create } from 'zustand';
import { engine } from '../engine/CanvasEngine';
import type { SelectionSummary, Tool } from '../types';
import type { StatusSnapshot } from '../engine/CanvasEngine';

interface UiState {
  tool: Tool;
  zoomPct: number;
  snapOn: boolean;
  gridOn: boolean;
  curved: boolean;
  canUndo: boolean;
  canRedo: boolean;
  selection: SelectionSummary;
  /** Bumped on every selection sync so uncontrolled forms remount with fresh values. */
  selVersion: number;
  status: StatusSnapshot;
  /** Bumped when the shape library/categories change, to re-render LibraryPanel. */
  libVersion: number;
  /**
   * Bumped on every document change. Panels that READ the whole drawing rather
   * than the selection — the formulation sheet, which is a reading of every
   * feeder at once — watch this to know the answer has moved.
   */
  docVersion: number;

  bumpLibrary: () => void;
  bumpDoc: () => void;
  syncTool: () => void;
  syncView: () => void;
  syncHistory: () => void;
  syncSelection: () => void;
  syncStatus: () => void;
}

export const useUiStore = create<UiState>((set) => ({
  tool: 'select',
  zoomPct: 100,
  snapOn: true,
  gridOn: true,
  curved: false,
  canUndo: false,
  canRedo: false,
  selection: { kind: 'none', count: 0 },
  selVersion: 0,
  status: { coordsX: 0, coordsY: 0, shapeCount: 0, connCount: 0, tool: 'select', selLabel: '' },
  libVersion: 0,
  docVersion: 0,

  bumpLibrary: () => set((s) => ({ libVersion: s.libVersion + 1 })),
  bumpDoc: () => set((s) => ({ docVersion: s.docVersion + 1 })),
  syncTool: () => set({ tool: engine.getTool() }),
  syncView: () => {
    const f = engine.getViewFlags();
    set({ zoomPct: engine.getZoomPct(), snapOn: f.snapOn, gridOn: f.gridOn, curved: f.curved });
  },
  syncHistory: () => set({ canUndo: engine.canUndo(), canRedo: engine.canRedo() }),
  syncSelection: () =>
    set((s) => ({ selection: engine.getSelectionSummary(), selVersion: s.selVersion + 1 })),
  syncStatus: () => set({ status: engine.getStatus() }),
}));
